import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, defs, linearGradient, stop } from "recharts";
import Graphics from "./Graphics";
// import graphics from '../../assets/icons/icon-bar-char-blue.png';
import document from '../../assets/icons/document.svg';
import * as S from './styles';
import api from "../../services/api";
import identifyErrorMenssage from "../../services/identifyErrorMenssage";
import downloadFile from "../../utils/downloadFile";
import { BodyG, BodyGSpan, H6, TaglineM } from "../../styles/stylesTypography";
import { theme } from "../../styles/theme";
import { ChartArea, Loading, IconeBotao } from './styles';
import { Line } from 'react-chartjs-2'
import { store } from 'react-notifications-component';
import { formatDate } from '../../../src/services/formatDate';

const TreatedAreaAgronomist = ({...props}) => {
    const [selectedOption, setSelectedOption] = useState(props.optionDefault ? props.optionDefault : "Área tratada (ha)");
    const [selectedDateFilter, setSelectedDateFilter] = useState(props.optionsDateFilter?.[0]?.value ?? "");
    const [loading, setLoading] = useState(true);
    const [totalTratada, setTotalTratada] = useState([]);
    const [areaTratadaPayload, setAreaTratadaPayload] = useState(null);
    const [graphicsData, setGraphicsData] = useState({ produtos: [], culturas: [], alvos: [] });

    const today = new Date();
    const priorDate = new Date(new Date().setDate(today.getDate() - 183));
    const [dataInicial, setDataInicial] = useState(priorDate.toISOString().slice(0, 10));
    const [dataFinal, setDataFinal] = useState(today.toISOString().slice(0, 10));
    const [dataGraph, setDataGraph] = useState([]);
    const [optionsGraph, setOptionsGraph] = useState([]);
    const [showChart, setShowChart] = useState(false);
    const [loadingXls, setShowLoadingXls] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);

    const tour = localStorage.getItem("startTour");

    const buildDayLabels = (dtInicialIso, total) => {
        const start = new Date(`${dtInicialIso}T00:00:00`);
        if (isNaN(start.getTime()) || !Number.isFinite(total) || total <= 0) return [];

        const out = [];
        for (let i = 0; i < total; i++) {
            const d = new Date(start.getTime());
            d.setDate(d.getDate() + i);
            out.push(d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }));
        }
        return out;
    };

    const buildMonthIsoLabels = (dtInicialIso, total) => {
        const start = new Date(`${dtInicialIso}T00:00:00`);
        if (isNaN(start.getTime()) || !Number.isFinite(total) || total <= 0) return [];

        const out = [];
        for (let i = 0; i < total; i++) {
            const d = new Date(start.getTime());
            d.setMonth(d.getMonth() + i);
            d.setDate(1);
            out.push(d.toISOString().slice(0, 10));
        }
        return out;
    };

    const buildLastDayLabels = (dtFinalIso, total) => {
        const end = new Date(`${dtFinalIso}T00:00:00`);
        if (isNaN(end.getTime()) || !Number.isFinite(total) || total <= 0) return [];

        const start = new Date(end.getTime());
        start.setDate(start.getDate() - (total - 1));

        const out = [];
        for (let i = 0; i < total; i++) {
            const d = new Date(start.getTime());
            d.setDate(d.getDate() + i);
            out.push(d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }));
        }
        return out;
    };

    const mapDateFilterToKey = (value) => {
        if (String(value) === "1") return "ultimos7dias";
        if (String(value) === "2") return "ultimos30dias";
        if (String(value) === "3") return "ultimoAno";
        return null;
    };

    const applySelectedDateFilterToReceitasRange = (value) => {
        const v = String(value ?? "");
        if (!v) return;

        const end = new Date();
        const endIso = end.toISOString().slice(0, 10);

        const start = new Date(end.getTime());
        if (v === "1") start.setDate(start.getDate() - 6);
        if (v === "2") start.setDate(start.getDate() - 29);
        if (v === "3") start.setDate(start.getDate() - 364);

        setDataInicial(start.toISOString().slice(0, 10));
        setDataFinal(endIso);
    };

    const buildReceitasGraphModel = (apiData, dateFilterValue) => {
        const payload = apiData ?? {};
        const root = payload?.data ?? payload;

        const key = mapDateFilterToKey(dateFilterValue);
        const slice = (key && root?.[key])
            ? root[key]
            : (root?.ultimos7dias ? root.ultimos7dias : root);

        const formatMonthShortLabel = (iso) => {
            const raw = String(iso ?? "");
            const withTime = raw.includes("T") ? raw : `${raw}T00:00:00`;
            const d = new Date(withTime);
            if (isNaN(d.getTime())) return raw;
            const mes = d.toLocaleDateString("pt-BR", { month: "short" }).toUpperCase().replace(".", "");
            const ano = d.getFullYear().toString().slice(-2);
            return `${mes}/${ano}`;
        };

        if (Array.isArray(slice?.dias) && Array.isArray(slice?.qtdReceita)) {
            const len = Math.min(slice.dias.length, slice.qtdReceita.length);
            const data = slice.qtdReceita.slice(0, len);
            let labels = buildLastDayLabels(slice.dtFinal, len);
            if (!labels.length) labels = buildDayLabels(slice.dtInicial, len);
            if (!labels.length) labels = slice.dias.slice(0, len).map((d) => String(d));

            return {
                labels,
                data,
                title: `Total de receitas emitidas de ${formatDate(slice.dtInicial)} à ${formatDate(slice.dtFinal)}`,
            };
        }

        if (Array.isArray(slice?.meses) && Array.isArray(slice?.qtdReceita)) {
            const len = Math.min(slice.meses.length, slice.qtdReceita.length);
            const data = slice.qtdReceita.slice(0, len);
            let labels = buildMonthIsoLabels(slice.dtInicial, len);
            if (!labels.length) labels = slice.meses.slice(0, len).map((m) => String(m));
            const prettyLabels = labels.map(formatMonthShortLabel);

            return {
                labels: prettyLabels,
                data,
                title: `Total de receitas emitidas de ${formatDate(slice.dtInicial)} à ${formatDate(slice.dtFinal)}`,
            };
        }

        if (Array.isArray(root?.meses) && Array.isArray(root?.qtdReceita)) {
            const len = Math.min(root.meses.length, root.qtdReceita.length);
            return {
                labels: root.meses.slice(0, len).map((m) => String(m)),
                data: root.qtdReceita.slice(0, len),
                title: `Total de receitas emitidas por mês de ${formatDate(dataInicial)} à ${formatDate(dataFinal)}`,
            };
        }

        return {
            labels: [],
            data: [],
            title: `Total de receitas emitidas por mês de ${formatDate(dataInicial)} à ${formatDate(dataFinal)}`,
        };
    };

    const searchInitialData = async () => {
        try {
            const endpoint = props.agronomoId
                ? `/relatorios/receitas/grafico/${props.agronomoId}`
                : '/relatorios/receitas/grafico';

            const response = await api.post(endpoint, {
                dtInicial: dataInicial,
                dtFinal: dataFinal
            });

            const apiData = response?.data ?? {};
            const model = buildReceitasGraphModel(apiData, selectedDateFilter);

            setDataGraph({
                labels: model.labels,
                datasets: [
                    {
                        label: 'Receitas',
                        data: model.data,
                        backgroundColor: 'rgb(47, 187, 105, 0.4)',
                        borderColor: 'rgb(47, 187, 105, 1)',
                        borderWidth: 1,
                        tension: 0.3,
                    },
                ],
            });

            setOptionsGraph({
                responsive: true,
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1,
                        },
                    },
                    x: {
                        ticks: {
                            font: {
                                size: 14,
                            },
                            stepSize: 1,
                        },
                    },
                },
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    title: {
                        display: true,
                        text: model.title,
                    },
                },
            });

            setInitialLoaded(true);
            setShowChart(true);
        } catch (error) {

        }
    }

    const updateData = async () => {
        if (initialLoaded) {
            try {
                const endpoint = props.agronomoId
                    ? `/relatorios/receitas/grafico/${props.agronomoId}`
                    : '/relatorios/receitas/grafico';

                const response = await api.post(endpoint, {
                    dtInicial: dataInicial,
                    dtFinal: dataFinal,
                })

                const apiData = response?.data ?? {};
                const model = buildReceitasGraphModel(apiData, selectedDateFilter);

                setDataGraph({
                    labels: model.labels,
                    datasets: [
                        {
                            label: 'Receitas',
                            data: model.data,
                            backgroundColor: 'rgb(47, 187, 105, 0.4)',
                            borderColor: 'rgb(47, 187, 105, 1)',
                            borderWidth: 1,
                            tension: 0.3,
                        },
                    ],
                });

                setOptionsGraph({
                    responsive: true,
                    indexAxis: 'x',
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                stepSize: 1,
                            },
                        },
                        x: {
                            ticks: {
                                font: {
                                    size: 14,
                                },
                                stepSize: 1,
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        title: {
                            display: false,
                            text: model.title,
                        },
                    },
                });

                setShowChart(true);
            } catch (error) {

            }
        }
    }

    const gerarXLS = async () => {
        const payload = {
            dtInicial: dataInicial,
            dtFinal: dataFinal,
        }

        try {
            setShowLoadingXls(true);
            window.onbeforeunload = () => 'Seu relatório está sendo gerado. Tem certeza que deseja sair?';

            const response = await api.post(`/relatorios/geral`, payload)

            if (response.data.downloadUrl) {
                await downloadFile(response.data.downloadUrl, 'relatorio-receitas.xlsx');
            }

            store.addNotification({
                title: "Sucesso!",
                message: "Relatório gerado com sucesso.",
                type: "success",
                insert: "top",
                container: "top-right",
                animationIn: ["animated", "fadeIn"],
                animationOut: ["animated", "fadeOut"],
                dismiss: {
                    duration: 6000,
                    onScreen: true
                }
            });

        } catch (error) {
            store.addNotification({
                title: "Erro!",
                message: identifyErrorMenssage(error, "ERRO-596 Erro ao gerar o relatorio - Tente novamente, se o erro persistir acione o suporte."),
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animated", "fadeIn"],
                animationOut: ["animated", "fadeOut"],
                dismiss: {
                    duration: 4000,
                    onScreen: true
                }
            });
        } finally {
            setShowLoadingXls(false);
            window.onbeforeunload = null;
        }
    }

    useEffect(() => {
        searchInitialData();
    }, [])

    useEffect(() => {
        updateData();
    }, [dataInicial, dataFinal])

    const normalizeAreaTratadaData = (raw) => {
        if (Array.isArray(raw)) return raw;
        if (!raw || typeof raw !== "object") return [];

        const pickValuesArray = (obj) => {
            const candidates = [
                obj?.qtdArea,
                obj?.qtd_area,
                obj?.area,
                obj?.areas,
                obj?.area_total,
                obj?.areaTotal,
                obj?.total_area,
                obj?.totalArea,
                obj?.valores,
                obj?.values,
                obj?.qtdReceita,
                obj?.qtdReceitas,
            ];
            return candidates.find((v) => Array.isArray(v)) ?? null;
        };

        if (Array.isArray(raw.data)) return raw.data;
        if (Array.isArray(raw.area_tratada)) return raw.area_tratada;
        if (Array.isArray(raw.areaTratada)) return raw.areaTratada;
        if (Array.isArray(raw.items)) return raw.items;

        const fromUltimos7dias = raw.ultimos7dias;
        if (fromUltimos7dias && typeof fromUltimos7dias === "object") {
            const labels = Array.isArray(fromUltimos7dias.dias) ? fromUltimos7dias.dias : null;
            const values = pickValuesArray(fromUltimos7dias);
            if (labels && values) {
                const len = Math.min(labels.length, values.length);
                const dayLabels = buildLastDayLabels(fromUltimos7dias.dtFinal, len) || [];
                return Array.from({ length: len }, (_, i) => ({
                    mes: dayLabels[i] ?? String(labels[i]),
                    total_area: values[i],
                }));
            }
        }

        if (Array.isArray(raw.dias)) {
            const values = pickValuesArray(raw);
            if (values) {
                const len = Math.min(raw.dias.length, values.length);
                const dayLabels = buildLastDayLabels(raw.dtFinal, len) || [];
                return Array.from({ length: len }, (_, i) => ({
                    mes: dayLabels[i] ?? String(raw.dias[i]),
                    total_area: values[i],
                }));
            }
        }

        if (Array.isArray(raw.meses)) {
            const values = pickValuesArray(raw);
            if (values && raw.dtInicial) {
                const len = Math.min(raw.meses.length, values.length);
                const monthLabels = buildMonthIsoLabels(raw.dtInicial, len) || [];
                return Array.from({ length: len }, (_, i) => ({
                    mes: monthLabels[i] ?? String(raw.meses[i]),
                    total_area: values[i],
                }));
            }
        }

        const meses = raw.meses || raw.labels || raw.mes || raw.months || raw.periodos;
        const valores =
            raw.total_area ||
            raw.totalArea ||
            raw.areas ||
            raw.valores ||
            raw.qtdArea ||
            raw.area_total ||
            raw.areaTotal;

        if (Array.isArray(meses) && Array.isArray(valores)) {
            const len = Math.min(meses.length, valores.length);
            return Array.from({ length: len }, (_, i) => ({
                mes: meses[i],
                total_area: valores[i],
            }));
        }

        if (raw.mes != null) {
            return [
                {
                    mes: raw.mes,
                    total_area:
                        raw.total_area ??
                        raw.totalArea ??
                        raw.area_total ??
                        raw.areaTotal ??
                        raw.valor ??
                        raw.value ??
                        0,
                },
            ];
        }

        return [];
    };

    const getAreaTratada = async () => {
        try {
            if (props.agronomoId) {
                const response = await api.get(`/area-tratada/${props.agronomoId}`);
                const raw = response?.data?.data ?? response?.data;
                setAreaTratadaPayload(raw);
            } else {
                const response = await api.get("/home/area-tratada");
                const raw = response?.data?.data ?? response?.data;
                setAreaTratadaPayload(raw);
            }
        } catch (error) {
        }
    };

    const getGraphicsData = async () => {
        try {
            // API limita o período dos relatórios a 24 meses (PERIODO_MAX_MESES)
            const now = new Date();
            const start = new Date(now);
            start.setUTCMonth(start.getUTCMonth() - 24);
            const requestBody = {
                dtInicial: start.toISOString().split('T')[0],
                dtFinal: now.toISOString().split('T')[0],
            };

            const [produtos, culturas, alvos] = await Promise.all([
                api.post('/relatorios/produtos/grafico', requestBody),
                api.post('/relatorios/culturas/grafico', requestBody),
                api.post('/relatorios/alvos/grafico', requestBody),
            ]);

            setGraphicsData({
                produtos: produtos.data.data,
                culturas: culturas.data.data,
                alvos: alvos.data.data,
            });

        } catch (error) {
        }
    };

    useEffect(() => {
        getAreaTratada();
        getGraphicsData();
    }, []);

    useEffect(() => {
        if (!props.optionsDateFilter) return;
        applySelectedDateFilterToReceitasRange(selectedDateFilter);
    }, [selectedDateFilter]);

    useEffect(() => {
        if (!areaTratadaPayload) return;
        const key = mapDateFilterToKey(selectedDateFilter);
        const slice = key && areaTratadaPayload?.[key] ? areaTratadaPayload[key] : areaTratadaPayload;
        setTotalTratada(normalizeAreaTratadaData(slice));
    }, [areaTratadaPayload, selectedDateFilter]);

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [selectedOption, selectedDateFilter]);

    const formatDataForChart = () => {
        if (!Array.isArray(totalTratada) || totalTratada.length === 0) return [];

        const toNumber = (v) => {
            if (typeof v === "number") return Number.isFinite(v) ? v : 0;
            const s = String(v ?? "").trim();
            if (!s) return 0;
            if (s.includes(",") && s.includes(".") && s.lastIndexOf(",") > s.lastIndexOf(".")) {
                const normalized = s.replace(/\./g, "").replace(",", ".");
                const n = Number(normalized);
                return Number.isFinite(n) ? n : 0;
            }
            if (s.includes(",") && !s.includes(".")) {
                const n = Number(s.replace(",", "."));
                return Number.isFinite(n) ? n : 0;
            }
            const n = Number(s);
            return Number.isFinite(n) ? n : 0;
        };

        const normalized = totalTratada
            .map((item, index) => {
                if (item == null) return null;

                if (typeof item === "number" || typeof item === "string") {
                    return {
                        mes: String(index + 1),
                        area_total: toNumber(item),
                    };
                }

                const rawMes =
                    item?.mes ??
                    item?.mes_label ??
                    item?.label ??
                    item?.periodo ??
                    item?.period ??
                    "";

                const rawValue =
                    item?.total_area ??
                    item?.area_total ??
                    item?.total ??
                    item?.valor ??
                    item?.value ??
                    0;

                const rawMesStr = String(rawMes);
                const iso = rawMesStr.includes("T") ? rawMesStr : `${rawMesStr}T00:00:00`;
                const d = new Date(iso);

                if (!isNaN(d.getTime())) {
                    const mes = d
                        .toLocaleDateString("pt-BR", { month: "short" })
                        .toUpperCase()
                        .replace(".", "");
                    const ano = d.getFullYear().toString().slice(-2);

                    return {
                        _date: d,
                        mes: `${mes}/${ano}`,
                        area_total: toNumber(rawValue),
                    };
                }

                if (!rawMesStr) return null;

                return {
                    mes: rawMesStr,
                    area_total: toNumber(rawValue),
                };
            })
            .filter(Boolean);

        if (normalized.length === 0) return [];

        if (normalized.length === 1 && normalized[0]._date) {
            const only = normalized[0];
            const prev = new Date(only._date.getTime());
            prev.setMonth(prev.getMonth() - 1);

            const mesPrev = prev.toLocaleDateString("pt-BR", { month: "short" }).toUpperCase().replace(".", "");
            const anoPrev = prev.getFullYear().toString().slice(-2);

            normalized.unshift({ _date: prev, mes: `${mesPrev}/${anoPrev}`, area_total: 0 });
        }

        return normalized.map(({ mes, area_total }) => ({ mes, area_total }));
    };

    const formatFakeDataForChart = () => {
        // Gera dados fictícios de jan/2025 até o mês atual
        const generateFakeData = () => {
            const out = [];
            const now = new Date();
            for (let y = 2025; y <= now.getFullYear(); y++) {
                const mStart = 0; // jan
                const mEnd = y === now.getFullYear() ? now.getMonth() : 11; // até o mês atual no ano corrente
                const dadosFixos = [180, 95, 150, 75, 120, 165, 88, 135, 110, 175, 92, 145];

                for (let m = mStart; m <= mEnd; m++) {
                    const dt = new Date(Date.UTC(y, m, 1)); // estável
                    out.push({
                        mes: dt.toISOString().slice(0, 10),   // "YYYY-MM-DD"
                        total_area: dadosFixos[m] ?? 100,
                    });
                }
            }
            return out;
        };

        const dataSource = (Array.isArray(totalTratada) && totalTratada.length > 0)
            ? totalTratada
            : generateFakeData();

        // Reusa a mesma lógica da função real para evitar divergência
        const temp = dataSource.map((item) => {
            const raw = String(item.mes);
            const iso = raw.includes("T") ? raw : `${raw}T00:00:00`;
            const d = new Date(iso);
            if (isNaN(d.getTime())) return null;

            const mes = d.toLocaleDateString("pt-BR", { month: "short" }).toUpperCase().replace(".", "");
            const ano = d.getFullYear().toString().slice(-2);
            return { _date: d, mes: `${mes}/${ano}`, area_total: item.total_area ?? 0 };
        }).filter(Boolean);

        if (temp.length === 0) return [];

        if (temp.length === 1) {
            const only = temp[0];
            const prev = new Date(only._date.getTime());
            prev.setMonth(prev.getMonth() - 1);

            const mesPrev = prev.toLocaleDateString("pt-BR", { month: "short" }).toUpperCase().replace(".", "");
            const anoPrev = prev.getFullYear().toString().slice(-2);

            temp.unshift({ _date: prev, mes: `${mesPrev}/${anoPrev}`, area_total: 0 });
        }

        return temp.map(({ mes, area_total }) => ({ mes, area_total }));
    };

    const areaTratada = () => {
        const data = formatDataForChart();

        return data.length === 0 ? (
            <>
                {tour === "true" ? (
                    <S.ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={formatFakeDataForChart()} margin={{ top: 20, right: 30, left: -10, bottom: 5 }}>
                                <defs>
                                    <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0.4} />
                                        <stop offset="100%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0} />
                                    </linearGradient>
                                </defs>

                                <XAxis
                                    dataKey="mes"
                                    tick={{ fontSize: 12, fill: "#21262F" }}
                                    tickLine={false}
                                    axisLine={false}
                                    padding={{ left: 10, right: 10 }}
                                />

                                <YAxis
                                    tick={{ fontSize: 12, fill: "#21262F" }}
                                    tickLine={false}
                                    axisLine={false}
                                />

                                <Tooltip />

                                <Area
                                    type="monotone"
                                    dataKey="area_total"
                                    stroke={theme.colors.green.Verde_AgroReceita}
                                    strokeWidth={2}
                                    fill="url(#colorGreen)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </S.ChartContainer>
                ) : (
                    <>
                        <H6 color={theme.colors.gray.Cinza_2} style={{ textAlign: "center" }}>
                            Emita seu primeiro Receituário para ver os relatórios
                        </H6>

                        <S.ButtonHeader onClick={() => window.location.href = "/receitas/add"}>
                            <img src={document} alt="Ícone de documento" />

                            <TaglineM color={theme.colors.white.Branco} fontWeight={800}>EMITIR NOVA RECEITA</TaglineM>
                        </S.ButtonHeader>
                    </>
                )}
            </>
        ) : (
            <S.ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 20, right: 30, left: -10, bottom: 5 }}>
                        <defs>
                            <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0.4} />
                                <stop offset="100%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={0} />
                            </linearGradient>
                        </defs>

                        <XAxis
                            dataKey="mes"
                            tick={{ fontSize: 12, fill: "#21262F" }}
                            tickLine={false}
                            axisLine={false}
                            padding={{ left: 10, right: 10 }}
                        />

                        <YAxis
                            tick={{ fontSize: 12, fill: "#21262F" }}
                            tickLine={false}
                            axisLine={false}
                        />

                        <Tooltip />

                        <Area
                            type="monotone"
                            dataKey="area_total"
                            stroke={theme.colors.green.Verde_AgroReceita}
                            strokeWidth={2}
                            fill="url(#colorGreen)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </S.ChartContainer>
        );
    };

    const problemasMaisFrequentes = () =>
        graphicsData.produtos.produtos?.length == 0 ? (
            <>
                <H6 color={theme.colors.gray.Cinza_2} style={{ textAlign: "center", marginTop: "40px" }}>
                    Emita seu primeiro Receituário para ver os relatórios
                </H6>

                <S.ButtonHeader style={{ marginBottom: "20px" }}>
                    <img src={document} alt="Ícone de documento" />

                    <TaglineM color={theme.colors.white.Branco} fontWeight={800}>EMITIR NOVA RECEITA</TaglineM>
                </S.ButtonHeader>
            </>
        ) : (
            <S.ContainerGraphics>
                {graphicsData.produtos && graphicsData.produtos.produtos?.length > 0 && (
                    <Graphics data={graphicsData.produtos} />
                )}

                {graphicsData.culturas && graphicsData.culturas.culturas?.length > 0 && (
                    <Graphics data={graphicsData.culturas} />
                )}

                {graphicsData.alvos && graphicsData.alvos.pests?.length > 0 && (
                    <Graphics data={graphicsData.alvos} />
                )}
            </S.ContainerGraphics>
        );

    const receitasEmitidas = () => {
        const hasReceitasData = Array.isArray(dataGraph?.labels) && dataGraph.labels.length > 0;

        if (!initialLoaded) {
            return (
                <S.ChartContainer>
                    <Loading />
                </S.ChartContainer>
            );
        }

        return !hasReceitasData ? (
            <>
                <H6 color={theme.colors.gray.Cinza_2} style={{ textAlign: "center" }}>
                    Emita seu primeiro Receituário para ver os relatórios
                </H6>

                <S.ButtonHeader onClick={() => window.location.href = "/receitas/add"}>
                    <img src={document} alt="Ícone de documento" />

                    <TaglineM color={theme.colors.white.Branco} fontWeight={800}>EMITIR NOVA RECEITA</TaglineM>
                </S.ButtonHeader>
            </>
        ) : (
            <S.ChartContainer>
                <>
                    <div>
                        <hr style={{
                            marginTop: '7px',
                            marginBottom: '0px'
                        }} />
                    </div>
                    {showChart && loadingXls == false ? (
                        <>

                            <ChartArea style={{
                                margin: 'auto',
                            }}>
                                <Line
                                    data={dataGraph}
                                    height={250}
                                    options={{
                                        plugins: {
                                            legend: {
                                                display: false,
                                            },
                                            title: {
                                                display: false,
                                                text: 'Receitas emitidas por mês',
                                            },
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: {
                                                    stepSize: 1,
                                                }
                                            }
                                        },
                                        maintainAspectRatio: false,

                                    }}
                                />
                            </ChartArea>
                        </>
                    ) : (<Loading />)}
                </>
            </S.ChartContainer>
        );
    };

    const renderOptions = {
        "Área tratada (ha)": areaTratada,
        "Problemas mais frequentes": problemasMaisFrequentes,
        "Receitas Emitidas": receitasEmitidas,
    };

    return (
        <S.Container className="treatedArea" isFrequentProblemsSelected={selectedOption === "Problemas mais frequentes"}>
            <S.Header>
                {tour === "true" ? (
                    <S.FarmSelect value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
                        <option value="Área tratada (ha)">Área tratada (ha)</option>
                    </S.FarmSelect>
                ) : (
                    <>
                        <BodyGSpan color={theme.colors.gray.Cinza_2} fontWeight={800}>
                            {props.textReport ? props.textReport : "Selecione o relatório"}
                        </BodyGSpan>

                        {props.optionsDateFilter ? (
                            <S.FarmSelect value={selectedDateFilter} onChange={(e) => setSelectedDateFilter(e.target.value)}>
                                {props.optionsDateFilter.map((item, index) => (
                                    <option key={index} value={item.value}>{item.label}</option>
                                ))}
                            </S.FarmSelect>
                        ) : (
                            <S.FarmSelect value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)}>
                                <option value="Área tratada (ha)">Área tratada (ha)</option>
                                <option value="Problemas mais frequentes">Problemas mais frequentes</option>
                                <option value="Receitas Emitidas">Receitas Emitidas</option>
                            </S.FarmSelect>
                        )}
                    </>
                )}
            </S.Header>

            <S.Body>
                {loading ? <S.Loading /> : renderOptions[selectedOption]?.()}
            </S.Body>
        </S.Container>
    );
};

export default TreatedAreaAgronomist;
